/**
 * Admin What's New — publish in-app update posts for all players.
 */
import {
  createAdminWhatsNewPost,
  deleteAdminWhatsNewPost,
  fetchAdminWhatsNewPosts,
  type AdminWhatsNewPostRow,
} from "../lib/cloud";

function formatSentAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return d.toISOString();
  }
}

export function mountAdminWhatsNew(root: HTMLElement): { refresh: () => void } {
  root.replaceChildren();

  const heading = document.createElement("h3");
  heading.className = "adminModerationHeading";
  heading.textContent = "What's New";
  root.appendChild(heading);

  const hint = document.createElement("p");
  hint.className = "accessHint";
  hint.textContent =
    "Publish an update post. Every player sees it once in a popup the next time they open CursorGame (until they dismiss it).";
  root.appendChild(hint);

  const status = document.createElement("p");
  status.className = "accessHint";
  root.appendChild(status);

  const form = document.createElement("div");
  form.className = "modeBox";

  const titleRow = document.createElement("div");
  titleRow.className = "formRow";
  const titleLabel = document.createElement("span");
  titleLabel.className = "formLabel";
  titleLabel.textContent = "Title";
  const titleInput = document.createElement("input");
  titleInput.className = "formInput";
  titleInput.type = "text";
  titleInput.maxLength = 120;
  titleInput.placeholder = "Update title";
  titleInput.setAttribute("aria-label", "What's New title");
  titleRow.append(titleLabel, titleInput);
  form.appendChild(titleRow);

  const bodyRow = document.createElement("div");
  bodyRow.className = "formRow";
  const bodyLabel = document.createElement("span");
  bodyLabel.className = "formLabel";
  bodyLabel.textContent = "Body";
  const bodyInput = document.createElement("textarea");
  bodyInput.className = "formInput whatsNewAdminBodyInput";
  bodyInput.rows = 8;
  bodyInput.maxLength = 20000;
  bodyInput.placeholder = "What's new in this update…";
  bodyInput.setAttribute("aria-label", "What's New body");
  bodyRow.append(bodyLabel, bodyInput);
  form.appendChild(bodyRow);

  const actions = document.createElement("div");
  actions.className = "adminSplashActions";
  const publishBtn = document.createElement("button");
  publishBtn.type = "button";
  publishBtn.className = "btn green";
  publishBtn.textContent = "Publish";
  const cancelBtn = document.createElement("button");
  cancelBtn.type = "button";
  cancelBtn.className = "btn";
  cancelBtn.textContent = "Clear";
  actions.append(publishBtn, cancelBtn);
  form.appendChild(actions);
  root.appendChild(form);

  const historyHeading = document.createElement("h4");
  historyHeading.className = "adminPlayerStatsSubheading";
  historyHeading.textContent = "Published posts";
  root.appendChild(historyHeading);

  const historyWrap = document.createElement("div");
  historyWrap.className = "leaderboardWrap";
  root.appendChild(historyWrap);

  let busy = false;
  let rows: AdminWhatsNewPostRow[] = [];

  function syncPublishEnabled() {
    const ok = !!titleInput.value.trim() && !!bodyInput.value.trim() && !busy;
    publishBtn.disabled = !ok;
  }

  function clearForm() {
    titleInput.value = "";
    bodyInput.value = "";
    syncPublishEnabled();
  }

  function renderHistory() {
    historyWrap.replaceChildren();
    if (!rows.length) {
      const empty = document.createElement("p");
      empty.className = "accessHint";
      empty.textContent = "No posts yet.";
      historyWrap.appendChild(empty);
      return;
    }
    for (const row of rows) {
      const card = document.createElement("div");
      card.className = "modeBox";
      card.style.marginBottom = "10px";

      const head = document.createElement("div");
      head.className = "whatsNewAdminCardHead";
      const t = document.createElement("strong");
      t.textContent = row.title;
      head.appendChild(t);
      if (!row.active) {
        const badge = document.createElement("span");
        badge.className = "whatsNewAdminInactiveBadge";
        badge.textContent = "Inactive";
        head.appendChild(badge);
      }
      const delBtn = document.createElement("button");
      delBtn.type = "button";
      delBtn.className = "btn red";
      delBtn.textContent = "Delete";
      delBtn.disabled = busy;
      delBtn.onclick = async () => {
        if (busy || !window.confirm("Delete this post permanently?")) return;
        busy = true;
        syncPublishEnabled();
        status.textContent = "Deleting…";
        try {
          await deleteAdminWhatsNewPost(row.id);
          rows = rows.filter((r) => r.id !== row.id);
          renderHistory();
          status.textContent = "Deleted.";
        } catch (e) {
          status.textContent =
            e instanceof Error && e.message === "forbidden" ? "Forbidden." : "Could not delete.";
        } finally {
          busy = false;
          syncPublishEnabled();
        }
      };
      head.appendChild(delBtn);
      card.appendChild(head);

      const desc = document.createElement("div");
      desc.className = "whatsNewAdminBodyPreview accessHint";
      desc.textContent = row.body;
      card.appendChild(desc);

      const meta = document.createElement("p");
      meta.className = "accessHint";
      meta.style.margin = "0";
      meta.textContent = formatSentAt(row.createdAt);
      card.appendChild(meta);

      historyWrap.appendChild(card);
    }
  }

  async function loadHistory() {
    status.textContent = "Loading…";
    try {
      rows = await fetchAdminWhatsNewPosts();
      renderHistory();
      status.textContent = "";
    } catch (e) {
      status.textContent =
        e instanceof Error && e.message === "forbidden" ? "Forbidden." : "Could not load posts.";
    }
  }

  titleInput.addEventListener("input", syncPublishEnabled);
  bodyInput.addEventListener("input", syncPublishEnabled);

  cancelBtn.onclick = () => {
    if (busy) return;
    clearForm();
    status.textContent = "";
  };

  publishBtn.onclick = async () => {
    const title = titleInput.value.trim();
    const body = bodyInput.value.trim();
    if (!title || !body || busy) return;
    busy = true;
    syncPublishEnabled();
    cancelBtn.disabled = true;
    status.textContent = "Publishing…";
    try {
      const post = await createAdminWhatsNewPost({ title, body, active: true });
      status.textContent = "Published. Players will see it on their next visit.";
      clearForm();
      rows = [post, ...rows.filter((r) => r.id !== post.id)];
      renderHistory();
    } catch (e) {
      status.textContent =
        e instanceof Error && e.message === "forbidden"
          ? "Forbidden."
          : e instanceof Error
            ? e.message
            : "Could not publish.";
    } finally {
      busy = false;
      cancelBtn.disabled = false;
      syncPublishEnabled();
    }
  };

  syncPublishEnabled();
  void loadHistory();

  return {
    refresh: () => {
      void loadHistory();
    },
  };
}
